import {useMemo} from 'react'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useI18n} from '@/i18n'
import {Systems, type PromotedSystemListTheme} from '@/pages/Systems'
import {usePromotedTypes, findPromoted, pickPromotedLabel, PROMOTABLE_ENTITY_TABLES} from '@/hooks/use-promoted-types'
import {useAppConfig, type LocalizedTypeValue} from '@/hooks/use-app-config'
import {pickTypeValueLabel} from '@/lib/promoted-types-label'
import type {ConceptKey} from '@/lib/concepts'
import type {PromotedTypeView} from '@/types/types'

export function SystemsByType() {
    const {typeCode} = useParams<{typeCode: string}>()
    const {promoted} = usePromotedTypes()
    const {systemTypes} = useAppConfig()
    const {i18n: i18nInstance} = useTranslation()
    const i18n = useI18n()
    const lang = i18nInstance.language
    const createPrefix = i18n.common.create

    const code = (typeCode ?? '').toUpperCase()
    const theme = useMemo<PromotedSystemListTheme | undefined>(
        () => buildTheme(promoted, systemTypes, code, lang, i18n.systems.allSystems, createPrefix),
        [promoted, systemTypes, code, lang, i18n.systems.allSystems, createPrefix],
    )

    return <Systems lockedType={code} themedAs={theme}/>
}

function buildTheme(
    promoted: PromotedTypeView[],
    systemTypes: LocalizedTypeValue[],
    code: string,
    lang: string,
    fallbackTitle: string,
    createPrefix: string,
): PromotedSystemListTheme | undefined {
    if (!code) return undefined
    const entry = findPromoted(promoted, PROMOTABLE_ENTITY_TABLES.SYSTEM_TYPES, code)
    if (!entry) {
        return {title: fallbackTitle}
    }
    const pluralLabel = pickPromotedLabel(entry, lang)
    const singularLabel = pickTypeValueLabel(systemTypes, code, lang) ?? pluralLabel
    return {
        title: pluralLabel,
        icon: entry.icon as ConceptKey | undefined,
        countLabel: ({count}) => `${count} ${singularLabel}`,
        createLabel: `${createPrefix} ${singularLabel}`,
    }
}
